/**
 * THE ONE LIST OF NAVIGABLE MENU ITEMS.
 *
 * Every menu item in the console is stored against a `menu_key`:
 * `menu_visibility_settings.menu_key` holds its global toggle and its chip,
 * `project_menu_visibility.menu_key` holds the per-project override, and
 * `user_groups.menu_permissions` holds the per-role grant. None of those
 * tables constrain the key — `PUT /api/menu-visibility/:menuKey` writes
 * whatever it is given — so the set of keys that actually EXIST is defined
 * here, in code, and nowhere else.
 *
 * It has to be one list because the toggles and the navigation are different
 * files. Five copies of this array had already drifted apart by 2026-08-27:
 * the sidebar shipped `usage` and `competitorAnalysis`, and neither had a
 * toggle on Platform Settings → Features, so two live admin screens could not
 * be hidden at all. The same page carried toggles for `salefishAdmin` and
 * `projects`, which no navigation has read for as long as either existed —
 * switches that wrote a row to the database and changed nothing on screen.
 * Drift in that direction is the worse one: a control that appears to work.
 *
 * So: add a menu item HERE and the sidebar, the Features toggles, the role
 * permission list, the per-project toggles and the admin home tile all pick it
 * up. `tests/navigationMenu.test.ts` fails if a consumer starts its own copy.
 */

/** Which sidebar context an item belongs to — `mode` in ModeAwareSidebar. */
export type MenuSectionId = "project" | "admin" | "company";

export interface NavMenuItem {
  /** The `menu_key` every toggle, chip and role permission is stored against. */
  key: string;
  /**
   * English label, and the fallback whenever `labelKey` is missing from a
   * language bundle.
   */
  label: string;
  /** Key in `Translations`, resolved with `t(labelKey, label)`. */
  labelKey?: string;
  /** Name resolved by `DynamicIcon` — lower-cased into its `iconMap`. */
  icon: string;
  /**
   * Sidebar destination. Project items are RELATIVE: the sidebar prefixes
   * `/projects/:projectId/`. Admin and company items are absolute.
   */
  url: string;
  section: MenuSectionId;
  /**
   * Shown to SUPER_ADMIN only (the server gates the data the same way). For
   * the Platform Settings sections PropPay reserves to platform owners — the
   * user log and the cross-company reports. An ADMIN never sees the row.
   */
  superAdminOnly?: boolean;
  /** Expandable sub-links. Same relative/absolute rule as `url`. */
  children?: ReadonlyArray<NavMenuChild>;
  /**
   * A tab of a grouped area rather than a menu row of its own. The sidebar
   * and the tab bar show the group once (`foldTabGroups`); the page shows the
   * open tabs. Each tab keeps its own key, so every toggle, chip and role
   * permission stored against it goes on working unchanged.
   */
  tabOf?: TabGroupId;
}

export interface NavMenuChild {
  title: string;
  titleKey?: string;
  /**
   * The label on a High-rise project, where `title` is the low-rise one.
   * Availability's unit page is "Lots" on low-rise and "Suites" on high-rise
   * (Andrew, 2026-10-26): it was "Inventory", which in new-home sales means a
   * home already sited, specced and built — a Quick Move-In.
   */
  highRiseTitle?: string;
  highRiseTitleKey?: string;
  url: string;
  /** Shown to project admins (SaleFish admins, company admins) only. */
  adminOnly?: boolean;
  /** Shown only to readers holding this permission (`pricing`: shared/permissions.ts canEditPricing). */
  requires?: "pricing";
  /** Not drawn on a high-rise project. */
  lowriseOnly?: boolean;
}

/**
 * The label a child link shows on this project. Every surface that names a
 * child (the sidebar, the breadcrumbs) asks here, so the two cannot disagree.
 */
export function navChildLabel(child: NavMenuChild, highRise: boolean): { title: string; titleKey?: string } {
  return highRise && child.highRiseTitle
    ? { title: child.highRiseTitle, titleKey: child.highRiseTitleKey }
    : { title: child.title, titleKey: child.titleKey };
}

export type TabGroupId = "sales";

/**
 * Areas whose sections are tabs (Sales & Contracts, Decisions 12 and 13): one
 * row in the navigation, open while any of its tabs is open to the reader.
 */
export const NAV_TAB_GROUPS: Record<TabGroupId, { key: TabGroupId; label: string; labelKey: string; icon: string }> = {
  sales: { key: "sales", label: "Sales", labelKey: "navSales", icon: "LayoutList" },
};

export const NAV_MENU_ITEMS: ReadonlyArray<NavMenuItem> = [
  // Project context — order is the sidebar's order.
  { key: "dashboard", label: "Dashboard", labelKey: "dashboard", icon: "Gauge", url: "dashboard", section: "project" },
  { key: "contacts", label: "Contacts", labelKey: "contacts", icon: "Contact", url: "contacts", section: "project" },
  { key: "tasks", label: "Tasks", labelKey: "tasks", icon: "CircleCheck", url: "tasks", section: "project" },
  { key: "emailMarketing", label: "Email Marketing", labelKey: "emailMarketing", icon: "Mails", url: "email-marketing", section: "project" },
  // The project website's tracker (Andrew, 2026-11-01): docs/plans/2026-11-01-website-analytics.md.
  { key: "websiteAnalytics", label: "Website Analytics", labelKey: "websiteAnalytics", icon: "MousePointerClick", url: "website-analytics", section: "project" },
  // Offers is its own row again (Andrew, 2026-10-25: "separate offers and Sales
  // like it was before"); Sales · Signing · Deposits stay one menu row (Sales &
  // Contracts plan, Decision 12). All keep the keys roles and toggles hold.
  { key: "offers", label: "Offers", labelKey: "offers", icon: "ClipboardPenLine", url: "offers", section: "project" },
  { key: "salesList", label: "Sales", labelKey: "navSalesTab", icon: "LayoutList", url: "sales-list", section: "project", tabOf: "sales" },
  { key: "salesManagement", label: "Deposits", labelKey: "navDepositsTab", icon: "Banknote", url: "deposits", section: "project", tabOf: "sales" },
  // Its own row again (Andrew, 2026-10-26: "this page should be linked to
  // 'Signing Queue' and in the main menu for all Company Admins"). Who sees it
  // is shared/menuGate.ts; only a signing officer can sign there.
  { key: "signingQueue", label: "Signing Queue", labelKey: "signingQueue", icon: "PenLine", url: "signing", section: "project" },
  {
    key: "availability", label: "Availability", labelKey: "availability", icon: "Grid2x2Check", url: "availability", section: "project",
    children: [
      // Order (Andrew, 2026-10-26): what sells first, then how the project is
      // built — homes, which go on which lots, their prices — and setup last.
      // The URL keeps "inventory" so every saved link still lands here.
      { title: "Lots", titleKey: "navAvailabilityLots", highRiseTitle: "Suites", highRiseTitleKey: "navAvailabilitySuites", url: "availability/inventory" },
      // Two pages, not one with tabs (Andrew, 2026-10-21): the homes brokers
      // can be shown (shared/listings.ts).
      { title: "Quick Move-in", titleKey: "shellNavQmis", url: "availability/qmis" },
      { title: "Outside Listings", titleKey: "shellNavOutsideListings", url: "availability/outside-listings" },
      { title: "Home Library", titleKey: "shellNavHomeLibrary", url: "availability/catalog" },
      // Which homes fit which lots, the home on each, and the architectural
      // controls between neighbours (plan §4.6, §4.7). Lowrise only: on a
      // highrise a suite's model is set on its sheet in Inventory (Andrew,
      // 2026-10-26: "Siting can be hidden on highrise").
      { title: "Siting", titleKey: "shellNavSiting", url: "availability/siting", lowriseOnly: true },
      // Price lists, premium and price rules, history (plan §4.5). Only for
      // the people who may change prices; the routes refuse everyone else.
      { title: "Pricing", titleKey: "shellNavPricing", url: "availability/pricing", requires: "pricing" },
      // Getting a project ready to sell (Availability plan phase 2): its layout, readiness, units.
      { title: "Setup", titleKey: "shellNavAvailabilitySetup", url: "availability/setup", adminOnly: true },
    ],
  },
  { key: "reports", label: "Reports", labelKey: "reports", icon: "ChartColumn", url: "reports", section: "project" },
  // The broker / agent portal, run from here (Andrew, 2026-10-21): only on a
  // project that is part of a client's Agent Portal (shared/menuGate.ts
  // hasAgentPortal), directly above Showroom, which keeps its place above
  // Project Settings. Its sections are tabs on the page.
  // Icon: lucide `user-group`.
  { key: "agentPortal", label: "Agent Portal", labelKey: "navAgentPortal", icon: "UserGroup", url: "agent-portal", section: "project" },
  // The sales-centre touch-screen app, run from here (Andrew, 2026-10-10). Only
  // on a project linked to a Showroom instance (shared/menuGate.ts hasShowroom),
  // directly above Project Settings. One item: its sections (Queue, Leads,
  // Analytics, Plans, Screens & setup) are tabs on the page, not sub-links
  // (Andrew, 2026-10-10). Icon: lucide `presentation`, the visitor screen.
  { key: "showroom", label: "Showroom", labelKey: "navShowroom", icon: "Presentation", url: "showroom", section: "project" },
  { key: "settings", label: "Project Settings", labelKey: "projectSettings", icon: "Cog", url: "settings", section: "project" },

  // Platform Settings (the SaleFish Admin context).
  // Sorted as PropPay sorts Platform Settings (Andrew, 2026-09-27): the
  // overview, then the organisations, the people, the money, the reports and
  // the connections — app-wide configuration last.
  { key: "usage", label: "Usage", labelKey: "shellNavUsage", icon: "Activity", url: "/admin/usage", section: "admin" },
  { key: "adminCompanies", label: "Companies", labelKey: "adminCompanies", icon: "BriefcaseBusiness", url: "/admin/companies", section: "admin" },
  // Every project — tiles, search, filters, edit (Andrew, 2026-09-27: the old
  // Projects page moved here; /projects is now the picker).
  { key: "adminProjects", label: "Projects", labelKey: "projectsTitle", icon: "grid", url: "/admin/projects", section: "admin" },
  { key: "users", label: "Users", labelKey: "adminUsers", icon: "Users", url: "/admin/users", section: "admin" },
  { key: "roles", label: "Roles", labelKey: "adminRoles", icon: "ShieldUser", url: "/admin/roles", section: "admin" },
  // Who did what, and which screens they opened — PropPay's User Log, its own
  // section rather than a tab inside Usage (Andrew, 2026-09-28).
  { key: "userLog", label: "User Log", labelKey: "shellNavUserLog", icon: "History", url: "/admin/user-log", section: "admin", superAdminOnly: true },
  { key: "adminBilling", label: "Billing & Proposals", labelKey: "adminBilling", icon: "Receipt", url: "/admin/billing", section: "admin" },
  // Cross-company reports (PropPay's platform Reports), separate from each
  // project's own Reports page. Usage → Reports folds into it.
  { key: "platformReports", label: "Reports", labelKey: "reports", icon: "ChartColumn", url: "/admin/reports", section: "admin", superAdminOnly: true },
  { key: "competitorAnalysis", label: "Competitor Analysis", labelKey: "shellNavCompetitorAnalysis", icon: "ChartPie", url: "/admin/competitor-analysis", section: "admin" },
  // The console rebuild and roadmap (Andrew, 2026-09-28): the same page as the
  // public /roadmap/<token> link, inside Platform Settings for staff. Filed
  // beside Competitor Analysis — both are strategy reports, not configuration.
  { key: "adminRoadmap", label: "Roadmap", labelKey: "shellNavRoadmap", icon: "Map", url: "/admin/roadmap", section: "admin" },
  { key: "integrations", label: "Integrations", labelKey: "adminIntegrations", icon: "Blocks", url: "/admin/integrations", section: "admin" },
  // SaleFish's own documents — terms, privacy, anything a builder is sent —
  // read and downloaded in the app (PropPay's Document Library).
  { key: "documentLibrary", label: "Document Library", labelKey: "shellNavDocumentLibrary", icon: "Library", url: "/admin/documents", section: "admin" },
  { key: "platformSettings", label: "General", labelKey: "adminPlatformSettings", icon: "SlidersHorizontal", url: "/admin/settings", section: "admin" },

  // Company Admin context.
  { key: "companyDetails", label: "Company Details", labelKey: "companyDetails", icon: "Building", url: "/company-admin", section: "company" },
  { key: "companyUsers", label: "Team Members", labelKey: "teamMembers", icon: "UsersRound", url: "/company-admin/users", section: "company" },
  { key: "companyFeatures", label: "Features & Billing", labelKey: "featuresBilling", icon: "CreditCard", url: "/company-admin/features", section: "company" },
  // A section of Company Settings since it had tabs; declared here once the
  // area became a modal whose rail reads this list (2026-09-28).
  { key: "companyProjects", label: "Projects", labelKey: "projectsTitle", icon: "Layers", url: "/company-admin/projects", section: "company" },
];

/** Section headings, in the order the Features page renders them. */
export const MENU_SECTIONS: ReadonlyArray<{ id: MenuSectionId; label: string; labelKey: string }> = [
  { id: "project", label: "Main Navigation", labelKey: "mainNavigation" },
  { id: "admin", label: "Platform Settings", labelKey: "platformSettings" },
  { id: "company", label: "Company Settings", labelKey: "companySettings" },
];

/**
 * The navigation as rows: each tab group folded into one row where its first
 * open tab stood, built by `make` from the group and its open tabs (in order).
 * Items that are not tabs pass through untouched.
 */
export function foldTabGroups<T extends { key: string }>(
  items: readonly T[],
  tabOfKey: (key: string) => TabGroupId | undefined,
  make: (group: (typeof NAV_TAB_GROUPS)[TabGroupId], tabs: T[]) => T,
): T[] {
  const out: T[] = [];
  const placed = new Set<TabGroupId>();
  for (const item of items) {
    const group = tabOfKey(item.key);
    if (!group) {
      out.push(item);
      continue;
    }
    if (placed.has(group)) continue;
    placed.add(group);
    out.push(make(NAV_TAB_GROUPS[group], items.filter((i) => tabOfKey(i.key) === group)));
  }
  return out;
}

export const tabGroupOf = (key: string): TabGroupId | undefined => NAV_MENU_ITEMS.find((i) => i.key === key)?.tabOf;

/** Old addresses of tabs that moved, for the router's redirects. */
export const MOVED_PROJECT_PAGES: Readonly<Record<string, string>> = {
  "signing-queue": "signing",
  "sales-management": "deposits",
};

export function menuItemsForSection(section: MenuSectionId): NavMenuItem[] {
  return NAV_MENU_ITEMS.filter((item) => item.section === section);
}

export const PROJECT_MENU_ITEMS = menuItemsForSection("project");
export const ADMIN_MENU_ITEMS = menuItemsForSection("admin");
export const COMPANY_MENU_ITEMS = menuItemsForSection("company");

/** Every `menu_key` the app navigates by. */
export const NAV_MENU_KEYS: string[] = NAV_MENU_ITEMS.map((item) => item.key);

/**
 * Spellings `user_groups.menu_permissions` still holds from before the keys
 * were camelCase, mapped to the key the navigation filters on.
 *
 * Every role saved before 2026-01-09 stored kebab-case (`email-marketing`,
 * `sales-list`), and the nav compares `menuPermissions.includes(item.key)`
 * against camelCase — so a role that granted Email Marketing in the old
 * spelling hid it. Checked against production 2026-09-28: "Sales Admin" (three
 * SaleFish staff, Andrew among them) and "Builder Admin" carried only
 * `email-marketing`, and Email Marketing had vanished for all of them on a
 * project that pays for it. Roles re-saved since carry both spellings, which is
 * why it looked intermittent.
 *
 * The data is left as it is and read through this, the same way
 * `companyType.ts` reads a short form an import can reintroduce: a role saved
 * by hand or copied from an old export must not quietly lose a menu.
 *
 * `worksheets` IS Offers — the page's key before it was renamed. This list
 * used to call it a dead key that "falls through harmlessly", and it was the
 * opposite: three roles (Sales Admin, Builder Admin, Sales) granted only
 * `worksheets`, so Offers was missing for everyone in them however the menu
 * toggles were set (Andrew, 2026-09-30).
 */
export const LEGACY_MENU_KEY_ALIASES: Readonly<Record<string, string>> = {
  "email-marketing": "emailMarketing",
  "sales-list": "salesList",
  "signing-queue": "signingQueue",
  "sales-management": "salesManagement",
  "project-settings": "settings",
  "salefish-admin": "salefishAdmin",
  worksheets: "offers",
};

/**
 * A role's menu permissions in the navigation's spelling, de-duplicated.
 * `null`/`undefined` stay as they are — no list means "everything", and an
 * empty list must not be turned into one that grants nothing new either.
 */
export function normalizeMenuPermissions<T extends readonly string[] | null | undefined>(keys: T): T {
  if (!keys) return keys;
  return Array.from(new Set(keys.map((k) => LEGACY_MENU_KEY_ALIASES[k] ?? k))) as unknown as T;
}
